import { AfterViewInit, Component, OnChanges, SimpleChanges, ViewEncapsulation, input, model, inject } from '@angular/core';
import { NgControl, ValidatorFn, Validators, FormsModule } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';

import { CurrencyCode, CurrencySymbol } from '@ap/amounts';
import { ApContentService } from '@ap/content';
import { ApIconComponent } from '@ap/icon';
import { ApFormErrorsComponent } from '@ap/form/errors';
import { AbstractInputComponent, ApAutocompleteModule, ApOptions } from '@ap/input';
import { ApInputConstraintService } from '@ap/input/constraint';
import { currencyFormatter } from '@ap/formatters';
import { ApSuggestionComponent } from '@ap/input/suggestion';
import { ApAmountsValidator, ApRangeValidator } from '@ap/input/validators';

import content from './amount-input.content.json';

@Component({
    selector: 'ap-amount-input',
    templateUrl: '../../../src/lib/input.component.html',
    styleUrls: ['../../../src/lib/input.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [FormsModule, ApIconComponent, ApSuggestionComponent, ApFormErrorsComponent, ApAutocompleteModule, NgTemplateOutlet],
    providers: [ApInputConstraintService]
})
export class ApAmountInputComponent extends AbstractInputComponent implements OnChanges, AfterViewInit {
    protected control = inject(NgControl, { self: true, optional: true });
    protected readonly inputConstraintService = inject(ApInputConstraintService);
    protected readonly contentService = inject(ApContentService);

    /**
     * Currency code.
     * Uses CurrencyCode enum from @ap/amounts
     */
    public readonly currencyCode = input<CurrencyCode>(CurrencyCode.EUR);

    /**
     * Should the input show the currency symbol.
     * Defaults to true. If set to false, shows currency code
     */
    public readonly showSymbol = input(true);

    /**
     * Amount of decimals allowed.
     * Defaults to 2.
     */
    public readonly numberOfDecimals = input(2);

    /**
     * Limit amount of total digits.
     * If not passed no limit is set.
     */
    public readonly maxDigits = input(12);

    /**
     * Icon is needed in the amount-input by ApIncomeComponent
     * However, do not add it to the documentation as we do not want other users to add an icon
     * @ignore
     */
    public readonly icon = input(null);

    /**
     * Minimal number that is allowed.
     * Validation is added if value is filled in
     */
    public readonly min = input<number>(null);

    /**
     * Maximum number that is allowed.
     * Validation is added if value is filled in
     */
    public readonly max = input<number>(null);

    /**
     * Additional validator if max or min is given
     * It can have a ApRangeValidator or Min or Max validator
     */
    private _validator: ValidatorFn;

    /**
     * Characters that are allowed to be filled in to the amount input
     * @ignore
     */
    protected readonly stripPattern = input<RegExp>(/[^0-9,.-]/g);

    /**
     * Currency code or symbol to prefix amount input with
     * @ignore
     */
    protected readonly prefix = model<string>(this.showSymbol() ? CurrencySymbol[this.currencyCode()] : this.currencyCode());

    override readonly options = input<ApOptions>(null);

    constructor() {
        super();
        this.setDefaultContent(content, 'amount-input');
    }

    /**
     * Set prefix on input changes
     */
    public ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        if (changes.prefix || changes.currencyCode || changes.showSymbol) {
            this.setPrefix();
        }
        if (changes.min || changes.max) {
            this.setValidatorMinMax();
        }
    }

    /**
     * After init bind the control validation
     */
    public ngAfterViewInit(): void {
        this.setValidatorMinMax();
        const control = this.control?.control;
        if (control) {
            control.addValidators([ApAmountsValidator.validate]);
            control.updateValueAndValidity();
        }
    }

    /**
     * On input change first strip all characters not allowed.
     * Then limit value to max amount of digits and decimals
     */
    protected override onInputChange(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        const allowedValue = this.inputConstraintService.strip(value, this.stripPattern());
        const limitedValue = this.limitValue(allowedValue);

        if (limitedValue !== value) {
            const { nativeElement } = this.input();
            const caret = nativeElement.selectionStart - 1;
            nativeElement.value = limitedValue;
            // prevent caret from jumping to end when infixing
            nativeElement.setSelectionRange(caret, caret);
        }

        this.value = limitedValue;

        this.onChangeWithFormatValue();
    }

    /**
     * The control value should always be the formatValue without any comma's
     * @protected
     */
    protected onChangeWithFormatValue() {
        this.onChange(this.value ? this.formatValue(this.value) : '');
    }

    /**
     * Format as currency
     */
    protected formatValue(value: string | number): string {
        const numberOfDecimalsValue = this.numberOfDecimals();
        const numberOfDecimals = numberOfDecimalsValue && numberOfDecimalsValue >= 0 ? numberOfDecimalsValue : 0;
        return currencyFormatter.format(value.toString(), numberOfDecimals);
    }

    /**
     * Format as currency for display
     */
    protected formatValueDisplay(value: string | number): string {
        const numberOfDecimalsValue = this.numberOfDecimals();
        const numberOfDecimals = numberOfDecimalsValue && numberOfDecimalsValue >= 0 ? numberOfDecimalsValue : 0;
        return currencyFormatter.formatDisplay(...[value.toString(), ...(numberOfDecimals === null ? [] : [numberOfDecimals])]);
    }

    /**
     * Set prefix based on if symbol or currency should be shown
     * @ignore
     */
    public setPrefix(): void {
        this.prefix.set(this.showSymbol() ? CurrencySymbol[this.currencyCode()] : this.currencyCode());
    }

    /**
     * Limit amount of digits total, and amount of decimals behind the seperator
     */
    private limitValue(value: string): string {
        const parts = value.split(',');
        const digits = this.limitDigits(parts[0], this.maxDigits());
        if (parts.length > 1 && this.numberOfDecimals() > 0) {
            return `${digits},${parts[1].replace(/[^\d]/g, '').substring(0, this.numberOfDecimals())}`;
        }
        return digits;
    }

    /**
     * Limit amount of digits
     */
    private limitDigits(value: string, maxDigits: number): string {
        // if maxDigits is n, then we should return the substring upto the nth digit
        // i.e. non-digit characters do not count in the substring length
        let digitCount = 0;
        let limitedValue = '';
        for (const char of value) {
            limitedValue += char;
            if (char.match(/\d/)) {
                digitCount++;
            }
            if (digitCount >= maxDigits) {
                return limitedValue;
            }
        }
        return limitedValue;
    }

    private setValidatorMinMax() {
        const control = this.control?.control;
        if (!control) {
            return;
        }
        if (control.hasValidator(this._validator)) {
            control.removeValidators(this._validator);
            this._validator = null;
        }
        const min = this.min();
        const max = this.max();
        if (this.isNumber(min) && this.isNumber(max)) {
            this._validator = ApRangeValidator.rangeValidator(min, max);
        } else if (this.isNumber(min) && !this.isNumber(max)) {
            this._validator = Validators.min(min);
        } else if (!this.isNumber(min) && this.isNumber(max)) {
            this._validator = Validators.max(max);
        }
        if (this._validator) {
            control.addValidators(this._validator);
        }
        control.updateValueAndValidity();
    }

    /**
     * Check if value is a valid number
     */
    private isNumber(value: number): boolean {
        return !isNaN(+value) && value !== null && value !== undefined;
    }
}
